import { inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { CustomerService } from '../customer.service';
import { CiamSessionExpiredError } from '../ciam/ciam.models';
import { GlobalResetService } from '../global-reset.service';
import { AddToBasketResult } from 'src/app/models/kingfisher-basket/add-to-basket-result';

/**
 * Ajout de la liste d'achats d'un configurateur 2D au panier e-commerce Castorama (Kingfisher).
 *
 * Le front n'envoie que la référence de la configuration : c'est le back (contrôleur `Basket` du
 * configurateur) qui dérive les produits (SKU + quantités) à partir du devis, puis appelle Kingfisher.
 *
 * L'état « projet ajouté » est mémorisé **par configuration** (id) : chaque projet a son propre état,
 * valable pour la session utilisateur courante, réinitialisé au prochain chargement (nouvelle session).
 */
@Injectable({ providedIn: 'root' })
export class KingfisherBasketService {
  private readonly http = inject(HttpClient);
  private readonly customer = inject(CustomerService);
  private readonly globalResetService = inject(GlobalResetService);

  /** Identifiant de la configuration ajoutée au panier durant la session (null si aucune). */
  private readonly _addedConfigurationId = signal<string | null>(null);

  constructor() {
    this.globalResetService.register(() => this.reset());
  }

  /**
   * Envoie la configuration courante au panier Castorama, via le contrôleur `Basket` du configurateur.
   * @param apiUrl URL de base de l'API du configurateur (`BaseApiService.apiUrl`).
   * @param configurationId Identifiant de la configuration à ajouter.
   */
  async addProjectToBasket(apiUrl: string, configurationId: string): Promise<AddToBasketResult> {
    // Jeton client Kingfisher (avec atg_profile_id), rafraîchi si besoin, et non `customer.token`
    // qui peut être écrasé par HBM/ByMe.
    const kingfisherToken = await this.customer.getValidKingfisherToken();
    if (!kingfisherToken) {
      // Sans jeton, la requête partirait en « Bearer undefined » et la gateway répondrait un 401
      // opaque au pire moment : à l'ajout au panier, en fin de configuration. On échoue
      // explicitement pour que l'appelant propose une reconnexion sans perdre le projet.
      throw new CiamSessionExpiredError();
    }

    const headers = new HttpHeaders({ Authorization: `Bearer ${kingfisherToken}` });
    const params = new HttpParams().set('id', configurationId);

    const result = await firstValueFrom(
      this.http.post<AddToBasketResult>(`${apiUrl}Basket/AddToBasket`, {}, { headers, params, withCredentials: true })
    );

    this._addedConfigurationId.set(configurationId);
    return result;
  }

  /** La configuration donnée a-t-elle déjà été ajoutée au panier durant la session ? */
  isAddedToBasket(configurationId: string): boolean {
    return !!configurationId && this._addedConfigurationId() === configurationId;
  }

  /** Réinitialise l'état « projet ajouté » (nouvelle session / reset global). */
  reset(): void {
    this._addedConfigurationId.set(null);
  }
}
